import { createSelector } from 'reselect';
import { array, string, option, ord, number, nonEmptyArray } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { LoyaltyMembership, LoyaltyProgramType } from '@mews-commander/data-access';
import { ApplicationState } from '@mews-commander/types';
import { Identifiable, createHandler } from '@mews/std';
import { createAction, createAsyncActionTypes } from '@mews/framework';
import prefix from '../prefix';
import parentSelector from '../selector';
import { createBaseEntitySelectors, createEntitiesReducer, createEntitySelectorByAccountId } from './utils';
import { getLoyaltyTiers } from './loyaltyTiers';
import { getLoyaltyPrograms } from './loyaltyPrograms';

export const NAME = 'loyaltyMemberships';

export const setLoyaltyMemberships = (entities: Array<LoyaltyMembership>) =>
    createAction(LOYALTY_MEMBERSHIPS_LOADED, { entities });

export const [getLoyaltyMemberships, getLoyaltyMembershipsByIds, getLoyaltyMembershipsByOwnerId, getLoyaltyMembership] =
    createBaseEntitySelectors<ApplicationState, LoyaltyMembership, typeof NAME>(parentSelector, NAME);

export const LOYALTY_MEMBERSHIPS_LOADED = prefix('LOYALTY_MEMBERSHIPS_LOADED');

export const LOYALTY_MEMBERSHIP_ADD = prefix('LOYALTY_MEMBERSHIP_ADD');
export const [LOYALTY_MEMBERSHIP_ADD_STARTED, LOYALTY_MEMBERSHIP_ADD_SUCCEEDED, LOYALTY_MEMBERSHIP_ADD_FAILED] =
    createAsyncActionTypes(LOYALTY_MEMBERSHIP_ADD);

export const [
    LOYALTY_MEMBERSHIP_UPDATE_STARTED,
    LOYALTY_MEMBERSHIP_UPDATE_SUCCEEDED,
    LOYALTY_MEMBERSHIP_UPDATE_FAILED,
] = createAsyncActionTypes(LOYALTY_MEMBERSHIP_ADD);

export default createEntitiesReducer({
    FETCH_SUCCESS: LOYALTY_MEMBERSHIPS_LOADED,
    ADD_SUCCESS: LOYALTY_MEMBERSHIP_ADD_SUCCEEDED,
    UPDATE_SUCCESS: LOYALTY_MEMBERSHIP_UPDATE_SUCCEEDED,
});

export const getLoyaltyMembershipsByAccountId = createEntitySelectorByAccountId(getLoyaltyMemberships);

const getProgramOrderByType = createHandler<LoyaltyProgramType, number>(
    {
        [LoyaltyProgramType.Hotel]: 0,
        [LoyaltyProgramType.ExternalPartner]: 1,
        [LoyaltyProgramType.SoftBrand]: 2,
    },
    3
);

interface SortingData extends Identifiable {
    name: string;
    type: LoyaltyProgramType;
    ordering: number;
}

const byProgramType: ord.Ord<SortingData> = pipe(
    number.Ord,
    ord.contramap(({ type }) => getProgramOrderByType(type))
);

const byTierOrdering: ord.Ord<SortingData> = pipe(
    number.Ord,
    ord.contramap(({ ordering }) => ordering)
);

const byProgramName: ord.Ord<SortingData> = pipe(
    string.Ord,
    ord.contramap(({ name }) => name)
);

export const getMostValuableLoyaltyMembership = createSelector(
    getLoyaltyMembershipsByAccountId,
    getLoyaltyTiers,
    getLoyaltyPrograms,
    (memberships, tiers, programs) =>
        pipe(
            memberships,
            array.map((membership) => ({
                id: membership.id,
                name: programs?.[membership.loyaltyProgramId].name,
                type: programs?.[membership.loyaltyProgramId].type,
                ordering: membership?.loyaltyTierId ? tiers?.[membership?.loyaltyTierId].ordering : 0,
            })),
            array.sortBy([byProgramType, byTierOrdering, byProgramName]),
            array.map(({ id }) => id),
            nonEmptyArray.fromArray,
            option.map(nonEmptyArray.head),
            option.toNullable
        )
);
